import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import { Button } from 'components/ui/button';
import { HttpError } from 'lib/https';
import { buildLearnedSkillPatch, learnedSkillErrorMessage } from './skills.service';
import { SkillDetailsModal } from './skill-details-modal';
import {
  getSkillDisplayName,
  type LearnedSkillDetails,
  type LearnedSkillDraft,
  type SkillDetails,
  type UpdateSkillPayload,
} from './skills.types';
import { useDeleteLearnedSkill, useGetLearnedSkill, useUpdateLearnedSkill } from './use-skills';

type LearnedSkillDetailsModalProps = {
  agentId: string;
  agentName: string;
  skillId: string | null;
  open: boolean;
  onClose: () => void;
  onUnavailable: () => void;
};

function toCatalogueSkill(skill: LearnedSkillDetails): SkillDetails {
  return {
    id: skill.id,
    name: getSkillDisplayName(skill),
    displayName: skill.displayName,
    description: skill.description,
    instructions: skill.instructions ?? '',
    version: null,
    iconUrl: null,
    tags: [],
    requiredToolIds: [],
    requiredIntegrationTypes: [],
    resources: [],
    scripts: [],
    scope: null,
    visibility: null,
    forceEnabled: false,
    installCount: 0,
    tenantId: '',
    createdBy: '',
    createdDate: skill.createdDate,
    lastUpdatedDate: skill.lastUpdatedDate,
    installed: false,
  };
}

function savedDraft(skill: LearnedSkillDetails): LearnedSkillDraft {
  return {
    displayName: getSkillDisplayName(skill),
    description: skill.description ?? '',
    instructions: skill.instructions ?? '',
  };
}

export function LearnedSkillDetailsModal({
  agentId,
  agentName,
  skillId,
  open,
  onClose,
  onUnavailable,
}: LearnedSkillDetailsModalProps) {
  const { t } = useTranslation();
  const skillQuery = useGetLearnedSkill(agentId, skillId, open);
  const updateSkill = useUpdateLearnedSkill(agentId);
  const deleteSkill = useDeleteLearnedSkill(agentId);
  const [revision, setRevision] = useState(0);
  const [conflict, setConflict] = useState('');
  const [pendingRevision, setPendingRevision] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const reportedMissing = useRef(false);
  const lockRevision = useRef(false);
  const data = skillQuery.data;

  useEffect(() => {
    setConflict('');
    setPendingRevision(null);
    setConfirmDelete(false);
    setEditorKey(0);
    reportedMissing.current = false;
    lockRevision.current = false;
  }, [skillId]);

  useEffect(() => {
    if (!data || lockRevision.current) return;
    setRevision(data.revision);
  }, [data]);

  useEffect(() => {
    if (!open || !skillId || reportedMissing.current) return;
    if (!(skillQuery.error instanceof HttpError) || skillQuery.error.status !== 404) return;
    reportedMissing.current = true;
    onUnavailable();
  }, [open, skillId, skillQuery.error, onUnavailable]);

  const save = async (payload: UpdateSkillPayload) => {
    if (!data || !skillId) return;
    const current = savedDraft(data);
    const next: LearnedSkillDraft = {
      displayName: (payload.name ?? current.displayName).trim(),
      description: (payload.description ?? current.description).trim(),
      instructions: payload.instructions ?? current.instructions,
    };
    if (!next.displayName || !next.description || !next.instructions.trim()) {
      throw new Error(
        t('LEARNED_SKILL_REQUIRED', {
          defaultValue: 'Display name, description, and instructions are required.',
        })
      );
    }
    const patch = buildLearnedSkillPatch(revision, current, next);
    if (!patch) return;

    try {
      const updated = await updateSkill.mutateAsync({ skillId, payload: patch });
      lockRevision.current = false;
      setRevision(updated.revision);
      setConflict('');
      setPendingRevision(null);
    } catch (error) {
      if (error instanceof HttpError && error.status === 409) {
        lockRevision.current = true;
        setConflict(
          learnedSkillErrorMessage(
            error,
            t('LEARNED_SKILL_CONFLICT', {
              defaultValue: 'This skill changed. Review it before saving again.',
            })
          )
        );
        const latest = await skillQuery.refetch();
        if (latest.data) setPendingRevision(latest.data.revision);
      }
      if (error instanceof HttpError && error.status === 404) onUnavailable();
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!skillId) return;
    try {
      await deleteSkill.mutateAsync(skillId);
      setConfirmDelete(false);
      onClose();
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        setConfirmDelete(false);
        onUnavailable();
      }
      throw error;
    }
  };

  const label = data ? getSkillDisplayName(data) : '';
  const banner = conflict ? (
    <div className="flex flex-col gap-2 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
      <p className="min-w-0 text-sm text-destructive">{conflict}</p>
      <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
        <Button
          type="button"
          variant="outline"
          className="h-10"
          disabled={pendingRevision == null}
          onClick={() => {
            if (pendingRevision == null) return;
            setRevision(pendingRevision);
            setPendingRevision(null);
            setConflict('');
          }}
        >
          {t('LEARNED_SKILL_KEEP_EDITS', { defaultValue: 'Keep my edits' })}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-10"
          onClick={() => {
            lockRevision.current = false;
            setPendingRevision(null);
            setConflict('');
            setEditorKey((key) => key + 1);
          }}
        >
          {t('LEARNED_SKILL_USE_LATEST', { defaultValue: 'Use latest' })}
        </Button>
      </div>
    </div>
  ) : null;

  return (
    <>
      <SkillDetailsModal
        key={editorKey}
        skillId={skillId}
        open={open}
        onClose={onClose}
        source={{
          variant: 'learned',
          skill: data ? toCatalogueSkill(data) : null,
          isLoading: skillQuery.isLoading,
          isError: skillQuery.isError,
          isSaving: updateSkill.isPending,
          onRetry: () => void skillQuery.refetch(),
          onUpdate: save,
          onDelete: data ? () => setConfirmDelete(true) : undefined,
          banner,
          meta: agentName
            ? t('LEARNED_FOR_AGENT', { defaultValue: 'Learned for {{agent}}', agent: agentName })
            : undefined,
          note: t('LEARNED_SKILL_EDIT_NOTE', {
            defaultValue:
              'Changes are guaranteed to apply in new conversations. This agent may refine this skill as it learns.',
          }),
        }}
      />
      <ConfirmationModal
        nested
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('LEARNED_SKILL_DELETE_TITLE', { defaultValue: 'Delete learned skill' })}
        description={t('LEARNED_SKILL_DELETE_DESCRIPTION', {
          defaultValue: 'Delete {{name}}? This permanently removes the skill. There is no undo.',
          name: label || t('SKILLS_THIS_SKILL', { defaultValue: 'this skill' }),
        })}
        onConfirm={handleDelete}
        confirmText={t('DELETE', { defaultValue: 'Delete' })}
        cancelText={t('CANCEL', { defaultValue: 'Cancel' })}
        confirmVariant="delete"
        preventAutoClose
        isLoading={deleteSkill.isPending}
      />
    </>
  );
}
